import { Head, router } from "@inertiajs/react";
import type { FormDataConvertible } from "@inertiajs/core";
import { useMemo, useState } from "react";
import ResourceTable from "@/components/resources/ResourceTable";
import ResourcePagination from "@/components/resources/ResourcePagination";
import ResourceFiltersPopover from "@/components/resources/ResourceFiltersPopover";
import ResourceSortControl from "@/components/resources/ResourceSortControl";
import ResourceTree from "@/components/resources/ResourceTree";
import { Input } from "@/components/ui/input";
import { PagyMeta } from "@/types/pagy";
import { useDeskSubnav } from "@/contexts/useDeskSubnav";

interface Column {
  key: string;
  label: string;
  type?: string;
  options?: Array<{ value: string; label: string }>;
}

interface Filter {
  key: string;
  type: "string" | "boolean" | "date" | "number" | "enum";
  label: string;
  operators?: string[];
  options?: Array<{ value: string; label: string }>;
}

interface Resource {
  name: string;
  label: string;
  view: string;
  views?: string[];
  tree?: {
    parent_key: string;
    type: "ancestry" | "parent_id";
    label_key?: string;
    code_key?: string;
  };
  columns: Column[];
  filters: Filter[];
  search: boolean;
  actions: string[];
  paths: {
    index: string;
    export: string;
    import: string;
    destroy: string;
    bulk_destroy: string;
    show?: string;
    update?: string;
  };
}

interface QueryState {
  q?: string;
  filters?: Array<{ field: string; operator: string; value: string }>;
  sort?: string;
  direction?: "asc" | "desc";
  tree?: "expanded" | "collapsed";
}

interface Props {
  resource: Resource;
  records: Array<Record<string, unknown>>;
  pagy: PagyMeta | null;
  query: QueryState;
}

export default function Index({ resource, records, pagy, query }: Props) {
  const { selectedIds, setSelectedIds } = useDeskSubnav();
  const [q] = useState(query.q || "");
  const [filters, setFilters] = useState<Array<{ field: string; operator: string; value: string }>>(
    query.filters || []
  );
  const defaultSort = resource.columns.find((column) => column.key === "created_at")?.key || "";
  const [sort, setSort] = useState(query.sort || defaultSort);
  const [direction, setDirection] = useState<"asc" | "desc">(query.direction || "desc");
  const views = resource.views?.length ? resource.views : [resource.view];
  const currentView = resource.view || views[0] || "list";
  const isTreeView = currentView === "tree";
  const expandAll = query.tree === "expanded";

  const serializeFilters = (items: Array<{ field: string; operator: string; value: string }>) =>
    items.map((item) => ({
      field: item.field,
      operator: item.operator,
      value: item.value,
    })) as Array<Record<string, FormDataConvertible>>;

  const params = useMemo(
    () => ({ q, filters: serializeFilters(filters), sort, direction, view: currentView }),
    [q, filters, sort, direction, currentView]
  );

  const defaultOperatorFor = (filter: Filter) => {
    if (filter.operators?.length) return filter.operators[0];
    switch (filter.type) {
      case "string":
        return "cont";
      case "boolean":
      case "enum":
        return "eq";
      case "date":
      case "number":
      default:
        return "eq";
    }
  };

  const applyInlineFilters = (
    nextFilters: Array<{ field: string; operator: string; value: string }>
  ) => {
    setFilters(nextFilters);
    router.get(
      resource.paths.index,
      { q, filters: serializeFilters(nextFilters), sort, direction, view: currentView },
      { replace: true, preserveState: true }
    );
  };

  const updateInlineFilterValue = (filter: Filter, value: string) => {
    const operator =
      filters.find((item) => item.field === filter.key)?.operator || defaultOperatorFor(filter);
    const next =
      value === ""
        ? filters.filter((item) => item.field !== filter.key)
        : [
            ...filters.filter((item) => item.field !== filter.key),
            { field: filter.key, operator, value },
          ];
    applyInlineFilters(next);
  };

  return (
    <>
      <Head title={resource.label} />

      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {!isTreeView && resource.filters.length > 0 ? (
            <div className="flex flex-wrap items-center gap-3">
              {resource.filters.map((filter) => {
                const active = filters.find((item) => item.field === filter.key);
                return (
                  <div key={filter.key} className="flex items-center">
                    {filter.type === "boolean" ? (
                      <select
                        className="h-9 min-w-[160px] rounded-md border border-input bg-input px-3 text-sm shadow-xs focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus:outline-none"
                        value={active?.value || ""}
                        onChange={(event) => updateInlineFilterValue(filter, event.target.value)}
                      >
                        <option value="">{filter.label}</option>
                        <option value="true">نعم</option>
                        <option value="false">لا</option>
                      </select>
                    ) : filter.type === "enum" ? (
                      <select
                        className="h-9 min-w-[180px] rounded-md border border-input bg-input px-3 text-sm shadow-xs focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus:outline-none"
                        value={active?.value || ""}
                        onChange={(event) => updateInlineFilterValue(filter, event.target.value)}
                      >
                        <option value="">{filter.label}</option>
                        {(filter.options || []).map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        type={
                          filter.type === "date"
                            ? "date"
                            : filter.type === "number"
                              ? "number"
                              : "text"
                        }
                        className="min-w-[180px]"
                        placeholder={filter.label}
                        value={active?.value || ""}
                        onChange={(event) => updateInlineFilterValue(filter, event.target.value)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div />
          )}
          <div className="flex flex-wrap items-center gap-3">
            {!isTreeView && resource.filters.length > 0 && (
              <ResourceFiltersPopover
                filters={resource.filters}
                value={filters}
                onApply={(nextFilters) => {
                  setFilters(nextFilters);
                  router.get(
                    resource.paths.index,
                    {
                      q,
                      filters: serializeFilters(nextFilters),
                      sort,
                      direction,
                      view: currentView,
                    },
                    { replace: true, preserveState: true }
                  );
                }}
                onClear={() => {
                  setFilters([]);
                  router.get(
                    resource.paths.index,
                    { q, filters: [], sort, direction, view: currentView },
                    { replace: true, preserveState: true }
                  );
                }}
              />
            )}
            {!isTreeView && resource.columns.length > 0 && (
              <ResourceSortControl
                options={resource.columns}
                value={sort}
                direction={direction}
                onChange={(nextSort, nextDirection) => {
                  setSort(nextSort);
                  setDirection(nextDirection);
                  router.get(
                    resource.paths.index,
                    {
                      q,
                      filters: serializeFilters(filters),
                      sort: nextSort,
                      direction: nextDirection,
                      view: currentView,
                    },
                    { replace: true, preserveState: true }
                  );
                }}
              />
            )}
          </div>
        </div>
        {currentView === "tree" && resource.tree ? (
          <div className="mx-auto w-full max-w-5xl px-2">
            <ResourceTree
              key={`tree-${expandAll}-${records.length}`}
              records={records}
              tree={resource.tree}
              expandAll={expandAll}
              showPath={resource.paths.show}
              destroyPath={resource.paths.destroy}
            />
          </div>
        ) : currentView === "table" || currentView === "list" ? (
          <ResourceTable
            columns={resource.columns}
            records={records}
            showPath={resource.paths.show}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            emptyLabel={`لا يوجد ${resource.label}`}
          />
        ) : (
          <div className="text-center py-10 text-muted-foreground">
            هذا المورد يستخدم عرضًا مخصصًا ({resource.view}).
          </div>
        )}

        {!isTreeView && pagy && (
          <ResourcePagination pagy={pagy} basePath={resource.paths.index} params={params} />
        )}
      </div>
    </>
  );
}
